import { LocalAudioTrack, LocalVideoTrack } from 'livekit-client';

export function requestedDeviceId(deviceId: unknown): string | undefined {
  if (typeof deviceId === 'string') return deviceId;
  if (deviceId && typeof deviceId === 'object') {
    const value = deviceId as { exact?: unknown; ideal?: unknown };
    if (typeof value.exact === 'string') return value.exact;
    if (typeof value.ideal === 'string') return value.ideal;
  }
  return undefined;
}

export function shouldSkipRedundantDeviceRestart(options: {
  requested?: string;
  current?: string;
  kind?: string;
}): boolean {
  const { requested, current, kind } = options;
  if (!current || !requested) return false;
  if (requested === current) return true;
  // Camera opened as "default" still reports a real deviceId. LiveKit then calls
  // setDeviceId("default") and restarts the track — black prejoin preview on macOS.
  // Audio must still switch to the OS default device from a headset or another mic.
  return kind === 'video' && requested === 'default';
}

/** A bare deviceId string is treated as {ideal}, which Chrome keeps ignoring. */
export function deviceIdConstraintForSwitch(requested?: string): string | { exact: string } | undefined {
  if (!requested) return requested;
  if (requested === 'default') return requested;
  return { exact: requested };
}

function patchSetDeviceId(ctor: typeof LocalAudioTrack | typeof LocalVideoTrack) {
  const proto = ctor.prototype as {
    setDeviceId: (deviceId: unknown) => Promise<boolean>;
    mediaStreamTrack?: MediaStreamTrack;
  };
  const original = proto.setDeviceId;
  if ((original as { __agimaSkipDefault?: boolean }).__agimaSkipDefault) return;

  async function wrapped(this: { mediaStreamTrack?: MediaStreamTrack }, deviceId: unknown) {
    const requested = requestedDeviceId(deviceId);
    const current = this.mediaStreamTrack?.getSettings?.().deviceId;
    if (
      shouldSkipRedundantDeviceRestart({
        requested,
        current,
        kind: this.mediaStreamTrack?.kind,
      })
    ) {
      return true;
    }
    return original.call(this, deviceIdConstraintForSwitch(requested) ?? deviceId);
  }
  (wrapped as { __agimaSkipDefault?: boolean }).__agimaSkipDefault = true;
  proto.setDeviceId = wrapped;
}

patchSetDeviceId(LocalAudioTrack);
patchSetDeviceId(LocalVideoTrack);
